import { isBoolean } from 'lodash';
import { STATE } from './constants';

export const hasCustomer = (ctx, { payload }) => {
  const hasContextCustomer = !!ctx.customer?.first_name;
  const hasPayloadCustomer = !!payload?.customer?.first_name;

  // If either context or payload has a customer with a first name, return true
  return hasContextCustomer || hasPayloadCustomer;
};

export const hasNoCustomer = (ctx, { payload }) => {
  return !hasCustomer(ctx, { payload });
};

export const hasActiveCustomer = (ctx, { payload }) => {
  return (
    payload.orderId ||
    payload.isActive ||
    payload.payload?.statusText === 'Created' ||
    payload.customer?.status === 'active' ||
    ctx.customer?.status === 'active'
  );
};

export const initialPetComplete = (ctx, { payload }) => {
  const pet = payload.pets.find(p => p.id === payload.pet_id);
  return (
    !!pet?.name && !!pet?.weight && !!pet?.activity_level && !!pet?.is_neutered
  );
};

export const hasPet = (ctx, { payload }) => {
  return (
    !!ctx.pet.id ||
    !!payload.pet?.id ||
    !!payload.pets?.[0]?.id ||
    payload.pet_id
  );
};

export const hasNoPet = (ctx, { payload }) => {
  return !hasPet(ctx, { payload });
};

export const hasPetName = (ctx, { payload }) => {
  return !!ctx?.pet?.name || !!payload.pet?.name;
};

export const hasPetGender = (ctx, action) => {
  return !!ctx?.pet?.gender;
};

export const hasPetStatus = ctx => {
  return ctx?.pet?.is_neutered !== null;
};

export const hasInitialPetWeight = (ctx, action) => {
  return !!ctx?.pet?.weight;
};

export const hasIncompletePetWithName = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.name;
  }
};

export const hasIncompletePetWithBirthday = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.birth_date;
  }
};

export const hasPetBirthday = (ctx, { payload }) => {
  return !!ctx.pet?.birth_date || !!payload.pet?.birth_date;
};

export const hasIncompletePetWithBreed = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet?.breeds?.length;
  }
};

export const hasPetBreed = (ctx, { payload }) => {
  return !!ctx.pet?.breeds?.length || !!payload.pet?.breeds?.length;
};

export const hasIncompletePetWithWeight = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.weight;
  }
};

export const hasPetWeight = (ctx, { payload }) => {
  return !!ctx.pet?.weight || !!payload.pet?.weight;
};

export const hasPetAllergyStatus = (ctx, { payload }) => {
  return ctx.pet?.allergies !== null || payload.pet?.allergies !== null;
};

export const hasAllergies = (ctx, { payload }) => {
  return !!ctx.pet?.allergies?.length || !!payload.pet?.allergies?.length;
};

export const hasIncompletePetWithActivity = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.activity_level;
  }
};

export const notGoBack = (ctx, action) => {
  return !(action.type === 'goBack');
};

export const inInitialFlow = (ctx, action) => {
  return !!ctx.initialFlow;
};

export const isInitialFlow = (ctx, action) => {
  return action.type === 'initialLoad' || ctx.initialFlow;
};

export const hasInitialPetActivity = (ctx, action) => {
  return !!ctx.pet?.activity_level;
};

export const hasPetActivity = (ctx, { payload }) => {
  return !!ctx.pet?.activity_level || !!payload.pet?.activity_level;
};

export const isCompletePet = pet => {
  const isComplete =
    !!pet.name &&
    !!pet.weight &&
    !!pet.activity_level &&
    isBoolean(pet.is_neutered);

  return isComplete;
};

export const hasCompletePet = (ctx, { payload }) => {
  const isComplete =
    !!isCompletePet(payload.pet) ||
    !!isCompletePet(ctx.pet) ||
    !!payload.pets?.find(pet => isCompletePet(pet)) ||
    !!ctx.pets?.find(pet => isCompletePet(pet));

  return isComplete;
};

export const allPetsComplete = (ctx, { payload }) => {
  const allComplete =
    (!!payload.pets?.length && payload.pets.every(pet => isCompletePet(pet))) ||
    (!!ctx.pets?.length && ctx.pets.every(pet => isCompletePet(pet)));

  return allComplete;
};

export const getFirstPetWithNoProductInCart = (ctx, { payload }) =>
  payload.pets.find(
    pet => !payload.cart.line_items.find(item => item.pet_id === pet.id),
  );

export const isAddPup = (ctx, { payload }) => !!payload.isAddPet;
export const isEditPet = (ctx, { payload }) => !!payload.isEditPet;
// Uses context since it's called after initial load and already integrated into context
export const notEditPet = (ctx, { payload }) => !ctx.isEditPet;

export const notAddPup = (ctx, { payload }) => !payload.isAddPet;

export const isDeletePet = (ctx, { payload }) => {
  return !!payload.isDeletePet;
};

export const notDeletePet = (ctx, { payload }) => !payload.isDeletePet;

export const allPetsHaveProductInCart = (ctx, { payload }) => {
  return (
    !!payload.pets?.length && payload.pets?.every(pet => !!pet.starter_box)
  );
};

export const notAllPetsHaveProductInCart = (ctx, { payload }) => {
  return !allPetsHaveProductInCart(ctx, { payload });
};

export const hasFreshInCart = (ctx, { payload: { recipes } }) =>
  !!recipes.fresh?.length;
export const hasDryInCart = (ctx, { payload: { recipes } }) =>
  !!recipes.dry?.length;
export const hasAddonsInCart = (ctx, { payload: { addons } }) =>
  !!addons.length;

export const hasMealPrep = (ctx, payload) => {
  return !!ctx?.pet?.starter_box || !!payload?.pet?.starter_box;
};
export const isFreshUrl = (ctx, { payload }) =>
  payload.pathname === STATE.fresh;
export const isBakedUrl = (ctx, { payload }) =>
  payload.pathname === STATE.baked;
export const isAddonsUrl = (ctx, { payload }) =>
  payload.pathname === STATE.addons;
export const prevStateBaked = ctx => ctx.prevState.stateKey === STATE.baked;
export const prevStateFresh = ctx => ctx.prevState.stateKey === STATE.fresh;
export const prevStateAddons = ctx => ctx.prevState.stateKey === STATE.addons;
export const submitAddonToCartFailed = (ctx, payload) => !payload.data.success;
export const submitCartFailed = (ctx, payload) => !payload.data.success;

//This function is correct now, don't destructure the payload here.
//This comment can be deleted once ES-1199 in completed- Dave
export const showRecommendedPlan = (ctx, action) => {
  return true;
};
export const skipRecommendedPlan = (ctx, payload) =>
  !showRecommendedPlan(ctx, payload);

export const personalizedPlan = (ctx, payload) => {
  return ctx.personalizedPlan ?? payload.personalizedPlan;
};

export const isAppSession = (ctx, { payload }) => {
  return ctx.isAppSession || payload?.isAppSession;
};
